import React from 'react'
import { useSetDefaultEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-set-default-email-options'
import { DisclosureBox } from 'shared/components/disclosure-box'
import { twMerge } from 'tailwind-merge'
import AttachmentsBlock from './components/attachments-block'
import ContactsSettingsBlock from './components/contacts-settings-block'
import EditorBlock from './components/editor-block'
import EmailSettingsBlock from './components/email-settings-block'
import SubstitutesBlock from './components/substitutes-block'
import { DashboardMailEditorProps, ExtendedState } from './types/common'

const DashboardMailEditor = <T extends ExtendedState>({
  additionalSubstitutes,
  attachmentsTempState,
  contactsSettings,
  errors,
  inModal,
  disabled,
  isPreFetching,
  isSwitchEditorButtonShown,
  onChangeContactsSettings,
  onChangeEmail,
  setAttachmentsTempState,
  showSettingsLink,
  sidebarItemBlock,
  switchEditorButtonProps,
  tempState,
  withContactsSettings,
  withRecipient,
  mailingId,
  withoutAttachments,
}: DashboardMailEditorProps<T>) => {
  const { isOptionsApplied } = useSetDefaultEmailOptions(tempState, onChangeEmail, {
    withRecipient,
  })

  return (
    <div className={'flex flex-col gap-3'}>
      {errors.common && (
        <>
          {errors.common.map((error, index) => (
            <p className="text-danger px-6 text-center text-sm" key={index}>
              {error}
            </p>
          ))}
        </>
      )}
      <div className={'flex flex-col gap-5 lg:flex-row'}>
        <div className={`min-w-0 lg:h-auto lg:basis-3/4`}>
          <EditorBlock
            additionalSubstitutes={additionalSubstitutes}
            isSwitchEditorButtonShown={isSwitchEditorButtonShown}
            switchEditorButtonProps={switchEditorButtonProps}
            isPreFetching={isPreFetching || !isOptionsApplied}
            disabled={disabled}
            tempState={tempState}
            withRecipient={withRecipient}
            onChangeEmail={onChangeEmail}
            inModal={inModal}
            errors={errors}
            showSettingsLink={showSettingsLink}
            mailingId={mailingId}
          />
        </div>
        <div
          className={`static top-5 flex h-fit flex-col gap-5 sm:min-w-[320px] lg:sticky lg:basis-1/4`}
        >
          <EmailSettingsBlock
            disabled={disabled}
            isPreFetching={isPreFetching || !isOptionsApplied}
            tempState={tempState}
            onChangeEmail={onChangeEmail}
            inModal={inModal}
            errors={errors}
          />

          {!withoutAttachments && (
            <AttachmentsBlock
              disabled={disabled}
              isPreFetching={isPreFetching}
              attachmentsTempState={attachmentsTempState}
              setAttachmentsTempState={setAttachmentsTempState}
              inModal={inModal}
              mailingId={mailingId}
              errors={errors?.attachments}
            />
          )}

          {sidebarItemBlock && (
            <DisclosureBox
              wrapperClassName={twMerge(
                inModal && 'border border-text-secondary/30 rounded-lg',
                sidebarItemBlock.showErrorOutline && 'border-danger',
              )}
              title={
                <b className={`text-text-primary text-start text-[17px]`}>
                  {sidebarItemBlock.title}
                </b>
              }
            >
              {sidebarItemBlock.Component}
            </DisclosureBox>
          )}

          {withContactsSettings && (
            <ContactsSettingsBlock
              disabled={disabled}
              isPreFetching={isPreFetching}
              contactsSettings={contactsSettings}
              onChangeContactsSettings={onChangeContactsSettings}
              inModal={inModal}
            />
          )}
          <SubstitutesBlock inModal={inModal} additionalSubstitutes={additionalSubstitutes} />
        </div>
      </div>
    </div>
  )
}

export default DashboardMailEditor
